import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { jobAPI } from '../../services/api';
import './ReportJobModal.css';

const ReportJobModal = ({ isOpen, onClose, jobId, jobTitle, onSuccess }) => {
  const { isAuthenticated } = useAuth();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [hideJob, setHideJob] = useState(true); // Default to checked (hide job)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Reset form when jobId changes or modal opens for a different job
  useEffect(() => {
    if (isOpen) {
      // Reset all form state when modal opens for a new job
      setReason('');
      setDescription('');
      setHideJob(true);
      setError(null);
      setSuccess(false);
      setLoading(false);
    }
  }, [jobId, isOpen]);

  const reportReasons = {
    spam: 'Spam or Repetitive Content',
    misleading: 'Misleading Information',
    inappropriate: 'Inappropriate Content',
    scam: 'Scam or Fraudulent',
    other: 'Other',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason) {
      setError('Please select a reason for reporting this job.');
      return;
    }

    if (!isAuthenticated) {
      setError('Please log in to report a job.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await jobAPI.report(jobId, {
        job_id: jobId,
        reason: reason,
        description: description.trim() || null,
      });

      if (response.success) {
        setSuccess(true);
        // Call onSuccess callback if provided (to refresh job list, mark as hidden, etc.)
        // Pass hideJob flag to the callback
        if (onSuccess) {
          onSuccess(jobId, hideJob);
        }
        setTimeout(() => {
          onClose();
          // Reset form
          setReason('');
          setDescription('');
          setHideJob(true); // Reset to default
          setSuccess(false);
        }, 2000);
      } else {
        setError(response.message || 'Failed to submit report. Please try again.');
      }
    } catch (err) {
      console.error('Error reporting job:', err);
      setError(err.message || 'Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setDescription('');
      setHideJob(true); // Reset to default
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="report-job-modal-overlay" onClick={handleClose}>
      <div className="report-job-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-job-modal-header">
          <h2>Report Job</h2>
          <button 
            className="report-job-modal-close"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="report-job-modal-content">
          {!isAuthenticated ? (
            <div className="report-job-modal-error">
              <p>Please log in to report a job.</p>
            </div>
          ) : success ? (
            <div className="report-job-modal-success">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <h3>Report Submitted</h3>
              <p>Thank you for reporting this job. Our team will review it shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="report-job-modal-body">
                <p className="report-job-modal-description">
                  Help us maintain a safe and trustworthy job board. Please select a reason for reporting <strong>"{jobTitle}"</strong>:
                </p>

                <div className="report-reason-group">
                  {Object.entries(reportReasons).map(([value, label]) => (
                    <label key={value} className="report-reason-option">
                      <input
                        type="radio"
                        name="reason"
                        value={value}
                        checked={reason === value}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={loading}
                      />
                      <span className="report-reason-label">{label}</span>
                    </label>
                  ))}
                </div>

                <div className="report-description-group">
                  <label htmlFor="description" className="report-description-label">
                    Additional Details (Optional)
                  </label>
                  <textarea
                    id="description"
                    className="report-description-textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Please provide any additional information that might help us review this report..."
                    rows="4"
                    maxLength="1000"
                    disabled={loading}
                  />
                  <div className="report-description-counter">
                    {description.length}/1000 characters
                  </div>
                </div>

                <div className="report-hide-job-group">
                  <label className="report-hide-job-option">
                    <input
                      type="checkbox"
                      checked={hideJob}
                      onChange={(e) => setHideJob(e.target.checked)}
                      disabled={loading}
                    />
                    <span className="report-hide-job-label">
                      Hide this job from my listings
                    </span>
                  </label>
                  <p className="report-hide-job-hint">
                    This job will be hidden from your job listings after reporting.
                  </p>
                </div>

                {error && (
                  <div className="report-job-modal-error-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="report-job-modal-footer">
                <button
                  type="button"
                  className="report-job-modal-cancel"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="report-job-modal-submit"
                  disabled={loading || !reason}
                >
                  {loading ? (
                    <>
                      <div className="spinner-small"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Report'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReportJobModal;

